import { createContext, useContext, useSyncExternalStore } from 'react';
import type { AppConfig } from '../config';
import type { MachineState } from '../domain/machine';
import type { AudioFeedback } from '../services/audio';
import type { RobotGateway, RobotStatus } from '../services/robot';
import type { SyncStatus } from '../services/sync';
import type { TabletSetup } from '../services/tablet-setup';
import type { AvailableUpdate } from '../services/update-check';
import type { Dispatch, MachineStore } from './store';

export interface AppContextValue {
  readonly store: MachineStore;
  readonly config: AppConfig;
  readonly audio: AudioFeedback;
  readonly robot: RobotGateway;
  /** Offline mode only: what is waiting on the tablet for the server. */
  readonly sync?: SyncIndicator | undefined;
  /** Offline mode only: setting the tablet up for a robot. */
  readonly setup?: TabletSetup | undefined;
  /** Android app only: a newer version on the install page. */
  readonly updates?: UpdateIndicator | undefined;
}

/** The part of the update check the screens read. */
export interface UpdateIndicator {
  available(): AvailableUpdate | null;
  subscribe(listener: () => void): () => void;
}

/** The part of the sync service the screens read. */
export interface SyncIndicator {
  /** The same object until something changes (React reads it as a snapshot). */
  status(): SyncStatus;
  subscribe(listener: () => void): () => void;
}

const NO_SYNC = () => () => undefined;

export const AppContext = createContext<AppContextValue | null>(null);

function useAppContext(): AppContextValue {
  const value = useContext(AppContext);
  if (!value) throw new Error('App hooks must be used inside <AppProvider>');
  return value;
}

export function useMachineState(): MachineState {
  const { store } = useAppContext();
  return useSyncExternalStore(store.subscribe, store.getState);
}

export function useDispatch(): Dispatch {
  return useAppContext().store.dispatch;
}

export function useConfig(): AppConfig {
  return useAppContext().config;
}

export function useAudio(): AudioFeedback {
  return useAppContext().audio;
}

export function useRobot(): RobotGateway {
  return useAppContext().robot;
}

/** Null unless the tablet runs offline mode. */
export function useSyncStatus(): SyncStatus | null {
  const { sync } = useAppContext();
  return useSyncExternalStore(
    sync ? (listener) => sync.subscribe(listener) : NO_SYNC,
    () => sync?.status() ?? null,
  );
}

/** A newer app on the install page; null when up to date, or not the Android app. */
export function useAvailableUpdate(): AvailableUpdate | null {
  const { updates } = useAppContext();
  return useSyncExternalStore(
    updates ? (listener) => updates.subscribe(listener) : NO_SYNC,
    () => updates?.available() ?? null,
  );
}

export function useTabletSetup(): TabletSetup | null {
  return useAppContext().setup ?? null;
}

/**
 * The robot and hospital shown on screen: in offline mode, the ones the tablet was set up for;
 * otherwise the build's.
 */
export function useUnitIdentity(): { readonly unitId: string; readonly hospitalName: string } {
  const config = useConfig();
  const sync = useSyncStatus();
  if (!sync) return config;
  return {
    unitId: sync.robot?.id ?? 'NEW TABLET',
    hospitalName: sync.robot?.hospitalName ?? (sync.robot ? '' : 'Not set up'),
  };
}

export function useRobotStatus(): RobotStatus {
  const { robot } = useAppContext();
  return useSyncExternalStore(
    (listener) => robot.subscribe(listener),
    () => robot.getStatus(),
  );
}
